import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table } from "@similarweb/pro-logic-components";
import { ScorableTab, TabList, Tabs } from "@similarweb/ui-components";
import { Box, Stack } from "@similarweb/ui-components/foundations";

import { useExtensionGovernanceUsersQuery } from "app/pages/sales-intelligence/modules/extension-management/query/queries/useExtensionGovernanceUsersQuery";
import { GOVERNANCE_BASE_URL } from "app/pages/sales-intelligence/modules/extension-management/services/api/factory";
import extensionManagementTrackingService from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";
import type { ExtensionGovernanceUser } from "app/pages/sales-intelligence/modules/extension-management/types";

import { SettingsSectionTitle } from "../SettingsSectionTitle/SettingsSectionTitle";

import { getGovernanceUsersColumns } from "./columns";
import { STATUS_FILTER_ALL, STATUS_FILTER_LABEL_MAP, STATUS_FILTER_OPTIONS, type StatusFilterValue } from "./constants";
import { computeStatusCounts, filterUsers } from "./helpers";
import { StyledTableContainer, StyledTabsWrapper } from "./styles";

const GOVERNANCE_USERS_EXPORT_URL = `${GOVERNANCE_BASE_URL}/users/export`;
const SEARCH_TRACK_DEBOUNCE_MS = 500;

export const GovernanceUsersTable = () => {
    const { translate } = useTranslate();
    const { data, isLoading } = useExtensionGovernanceUsersQuery();
    const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(STATUS_FILTER_ALL);
    const [searchTerm, setSearchTerm] = useState("");

    const users = useMemo(() => data?.items ?? [], [data?.items]);

    const statusCounts = useMemo(() => computeStatusCounts(users), [users]);

    const selectedFilterIndex = STATUS_FILTER_OPTIONS.indexOf(statusFilter);

    const handleFilterChange = useCallback((index: number) => {
        const filterValue = STATUS_FILTER_OPTIONS[index];
        if (!filterValue) {
            return;
        }
        extensionManagementTrackingService.trackStatusFilterChange(filterValue);
        setStatusFilter(filterValue);
    }, []);

    const searchTrackTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const handleSearchChange = useCallback((value: string) => {
        clearTimeout(searchTrackTimerRef.current);
        searchTrackTimerRef.current = setTimeout(() => {
            extensionManagementTrackingService.trackSearchUsers();
        }, SEARCH_TRACK_DEBOUNCE_MS);
        setSearchTerm(value);
    }, []);

    useEffect(() => {
        return () => clearTimeout(searchTrackTimerRef.current);
    }, []);

    const filteredUsers = useMemo(
        () => filterUsers(users, statusFilter, searchTerm),
        [users, statusFilter, searchTerm],
    );

    const tableData = useMemo(
        () => filteredUsers.map((user) => ({ ...user, key: user.userId, jobTitle: user.jobTitle ?? "" })),
        [filteredUsers],
    );

    const columnConfig = useMemo(() => getGovernanceUsersColumns(), []);

    return (
        <Stack data-automation="extension-governance-users-table-section">
            <Box padding="0 28px 16px">
                <SettingsSectionTitle
                    title={translate("si.management.extension.governance.title")}
                    description={translate("si.management.extension.governance.description")}
                    dataAutomation="extension-governance-users-table-section"
                />
            </Box>
            <StyledTabsWrapper>
                <Tabs
                    selectedIndex={selectedFilterIndex}
                    onSelect={handleFilterChange}
                >
                    <TabList>
                        {STATUS_FILTER_OPTIONS.map((filterValue) => (
                            <ScorableTab
                                key={filterValue}
                                metric={translate(STATUS_FILTER_LABEL_MAP[filterValue])}
                                value={String(statusCounts[filterValue])}
                            />
                        ))}
                    </TabList>
                </Tabs>
            </StyledTabsWrapper>
            <StyledTableContainer data-automation="extension-governance-users-table">
                <Table<ExtensionGovernanceUser>
                    data={tableData}
                    columnConfig={columnConfig}
                    isLoading={isLoading}
                    pagination={{ pageSize: 20 }}
                    withArrowButtons={false}
                >
                    <Table.TableTop borderBottom>
                        <Table.TableTop.Filters
                            filters={[
                                {
                                    filter: "search",
                                    initialValue: searchTerm,
                                    onSearchChange: handleSearchChange,
                                    placeholder: translate("si.management.extension.governance.search.placeholder"),
                                },
                            ]}
                        />
                        <Table.TableTop.Settings
                            excelExportConfig={{
                                excelDownloadUrl: GOVERNANCE_USERS_EXPORT_URL,
                                onExcelDownloaded: () => extensionManagementTrackingService.trackExportExcel(),
                            }}
                        />
                    </Table.TableTop>
                </Table>
            </StyledTableContainer>
        </Stack>
    );
};
